import { Platform, StyleSheet, StyleProp, ViewStyle, DimensionValue } from "react-native";
import { useTheme } from "@/hooks/useTheme";
import { useState, useEffect } from "react";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  SharedValue,
  runOnJS,
  interpolate,
  Extrapolation,
  withSpring,
  Easing,
} from "react-native-reanimated";
import { Gesture, GestureDetector } from "react-native-gesture-handler";

export type HorizontalScrollbarProps = {
  scrollX: SharedValue<number>; // Current scroll position
  contentWidth: number; // Total scrollable content width
  containerWidth: number; // Visible container width
  onScroll?: (scrollX: number) => void; // Callback for scroll changes
  style?: StyleProp<ViewStyle>;
  thumbStyle?: StyleProp<ViewStyle>;
  trackStyle?: StyleProp<ViewStyle>;
  autoHide?: boolean; // Default true
  thumbMinWidth?: number; // Minimum thumb width
  height?: number; // Scrollbar height
  leftMargin?: DimensionValue;
  rightMargin?: DimensionValue;
  bottom?: DimensionValue; // Distance from bottom
};

export default function HorizontalScrollbar({
  scrollX,
  contentWidth,
  containerWidth,
  onScroll,
  style,
  thumbStyle,
  trackStyle,
  autoHide = true,
  thumbMinWidth = 30,
  height = 12,
  leftMargin = 0,
  rightMargin = 0,
}: HorizontalScrollbarProps) {
  const { colors } = useTheme();
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Animation values
  const opacity = useSharedValue(autoHide ? 0 : 0.8);
  const trackOpacity = useSharedValue(0.3);

  // Calculate scrollbar dimensions
  const maxScrollX = Math.max(0, contentWidth - containerWidth);

  // Calculate actual usable width considering rightMargin
  const rightMarginValue = typeof rightMargin === "number" ? rightMargin : 0;
  const availableWidth = containerWidth - rightMarginValue;
  const trackWidth = availableWidth - 20; // 10px padding on each side

  // Simple scrollbar logic: thumb size represents visible portion of content
  const thumbWidth = Math.max(thumbMinWidth, trackWidth * (containerWidth / contentWidth));
  const maxThumbX = trackWidth - thumbWidth;

  // Show/hide logic
  useEffect(() => {
    if (!autoHide) return;

    if (isDragging || isHovered) {
      opacity.value = withSpring(1, { damping: 20, stiffness: 300 });
      trackOpacity.value = withTiming(0.5, { duration: 200 });
    } else {
      const timer = setTimeout(() => {
        opacity.value = withTiming(0.7, {
          duration: 400,
          easing: Easing.out(Easing.cubic),
        });
        trackOpacity.value = withTiming(0.2, { duration: 400 });
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [isDragging, isHovered, autoHide, opacity, trackOpacity]);

  // Animated styles
  const containerAnimatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateY: withSpring(0, { damping: 15 }) }],
  }));

  const trackAnimatedStyle = useAnimatedStyle(() => ({
    opacity: trackOpacity.value,
  }));

  const thumbAnimatedStyle = useAnimatedStyle(() => {
    const thumbX = interpolate(scrollX.value, [0, maxScrollX], [0, maxThumbX], Extrapolation.CLAMP);

    return {
      transform: [{ translateX: thumbX }],
      elevation: isDragging ? 8 : 4,
    };
  });

  // Gesture handling
  const panGesture = Gesture.Pan()
    .onChange((event) => {
      // Convert thumb movement to scroll movement
      // If thumb moves its full range (maxThumbX), content should scroll its full range (maxScrollX)
      const thumbMovementRatio = maxScrollX / maxThumbX; // How much content scrolls per thumb pixel
      const deltaScroll = event.changeX * thumbMovementRatio;
      const newScrollX = Math.max(0, Math.min(maxScrollX, scrollX.value + deltaScroll));

      scrollX.value = newScrollX;
      if (onScroll) {
        runOnJS(onScroll)(newScrollX);
      }
    })
    .onBegin(() => {
      runOnJS(setIsDragging)(true);
      // No scaling - just state change for color feedback
    })
    .onEnd(() => {
      runOnJS(setIsDragging)(false);
    });

  // Hover handlers for web
  const handleHoverIn = () => {
    if (Platform.OS === "web") {
      setIsHovered(true);
      // No scaling on hover, just state change for opacity
    }
  };

  const handleHoverOut = () => {
    if (Platform.OS === "web") {
      setIsHovered(false);
    }
  };

  // Always render container to prevent layout jumps, but conditionally render content
  const shouldShowScrollbar = contentWidth > containerWidth;

  return (
    <Animated.View
      style={[
        styles.container,
        {
          height,
          marginLeft: leftMargin,
          width: availableWidth, // Use calculated width instead of marginRight
        },
        containerAnimatedStyle,
        style,
      ]}
    >
      {shouldShowScrollbar && (
        <>
          {/* Track */}
          <Animated.View
            style={[
              styles.track,
              {
                backgroundColor: colors.border,
                width: trackWidth,
                height: Platform.OS === "web" ? height : height * 0.3,
              },
              trackAnimatedStyle,
              trackStyle,
            ]}
          />

          {/* Thumb */}
          <GestureDetector gesture={panGesture}>
            <Animated.View
              onPointerEnter={handleHoverIn}
              onPointerLeave={handleHoverOut}
              style={[
                //@ts-ignore
                styles.thumb,
                {
                  width: thumbWidth,
                  height: Platform.OS === "web" ? height * 0.8 : height * 0.7,
                  backgroundColor: isDragging ? colors.primary : colors.border,
                  opacity: isDragging ? 1 : 0.8,
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.2)",
                },
                thumbAnimatedStyle,
                thumbStyle,
              ]}
            />
          </GestureDetector>
        </>
      )}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    justifyContent: "center",
    alignItems: "flex-start",
    zIndex: 20,
    elevation: 20,
  },
  track: {
    position: "absolute",
    borderRadius: 4,
    left: 10,
    ...Platform.select({
      web: {
        // background: "linear-gradient(to right, transparent, rgba(0,0,0,0.1), transparent)",
      },
    }),
  },
  thumb: {
    position: "absolute",
    borderRadius: 6,
    left: 10,
    cursor: Platform.OS === "web" ? "grab" : undefined,
    // Subtle border for definition
    borderWidth: Platform.OS === "web" ? 0.5 : 0,
    borderColor: "rgba(255,255,255,0.1)",
  },
});
